/**
 * **경로 → 화면 id.** 표는 `constants/analytics-pages`(feature-map 의 routes 에서 생성)다 — 손으로 적지 않는다.
 *
 * 규칙: 마디 수가 같고 마디마다 같거나(정확) 표의 마디가 `[id]` 같은 동적 마디면 맞는다. 마지막 마디가 `**` 인 패턴(관리자
 * `/admin/**` 하나)만 그 아래 전부를 받는다. 그 밖의 **접두 일치는 하지 않는다** —
 * `/service/모르는-화면` 을 `dashboard`(`/service`)로 세면 새 화면이 통계에 조용히 섞인다. 표에 없으면 `null` 이고,
 * 그 NULL 이 「feature-map 에 등록 안 된 화면」의 신호다.
 * 🔑 옛 서비스 주소(`/service/brand-acc` 등 · 308 로 넘어가는 것)는 **보내질 새 주소의 화면**으로 센다
 *   (`routes/legacy-redirects`) — 넘어가기 전에 찍힌 로그도 같은 화면으로 모인다.
 */

import type { AnalyticsPage } from 'src/types/analytics/events';

import { resolveLegacyRedirect } from 'src/routes/legacy-redirects';

import { ANALYTICS_PAGE_ROUTES } from 'src/constants/analytics-pages';

// ----------------------------------------------------------------------

type Compiled = { page: AnalyticsPage; segments: string[] };

let compiled: Compiled[] | null = null;

function compile(): Compiled[] {
  if (!compiled) {
    compiled = ANALYTICS_PAGE_ROUTES.map(([route, page]) => ({ page, segments: split(route) }));
  }
  return compiled;
}

function split(path: string): string[] {
  return path.split('?')[0].split('/').filter(Boolean);
}

function segmentMatches(pattern: string, actual: string): boolean {
  return pattern.startsWith('[') || pattern === actual;
}

/** @returns 화면 id. 모르는 경로는 `null` */
export function pageOfPath(path: string): AnalyticsPage | null {
  if (!path) return null;
  return matchPage(path) ?? matchPage(resolveLegacyRedirect(path) ?? '');
}

function matchPage(path: string): AnalyticsPage | null {
  if (!path) return null;
  const actual = split(path);
  let best: Compiled | null = null;
  for (const entry of compile()) {
    const wildcard = entry.segments[entry.segments.length - 1] === '**';
    const fixed = wildcard ? entry.segments.slice(0, -1) : entry.segments;
    if (wildcard ? actual.length < fixed.length : fixed.length !== actual.length) continue;
    if (!fixed.every((seg, i) => segmentMatches(seg, actual[i]))) continue;
    // 길이가 같은 후보가 둘이면 동적 마디가 없는 쪽(정확 일치)을 택한다
    if (!best || !entry.segments.some((s) => s.startsWith('['))) best = entry;
  }
  return best?.page ?? null;
}
